<template>
  <li class="w-100">
    <div :class="rootClass" class="px-2 py-2 d-flex align-items-center font-size">
      <div class="mr-auto truncate pointer" :title="item.name" @click="goToResource">
          <font-awesome-icon :icon="displayIcon" />
          {{ item.name }}
      </div>
      <div class="text-right pointer">
        <font-awesome-icon v-if="!loading && hasChildren" :icon="caretDisplay" @click="toggleChildren" />
        <b-spinner v-if="loading" small label="spinning" />
      </div>
    </div>
    <div class="w-100 child-indent">
		<div class="w-100 px-2 py-2 input-group input-group-sm" v-if="showChildren && root">
			<b-form-input :placeholder="'Search parent groups'" type="search" v-model="searchGroupInput" @keyup.enter="updateSearchTerm" @input="searchBoxUpdated" />
			<div class="input-group-append pointer" @click="updateSearchTerm">
				<span class="input-group-text" id="basic-addon1">
					<font-awesome-icon :icon="'search'" />
				</span>
			</div>
		</div>
      <!-- root means we are at the client level; this first instance will be the unassigned group -->
      <sidebar-tree :client-id="clientId" :item="unassigned" v-if="showChildren && root" resource="unassigned" />
      <!-- this second instance will be all other child groups within the parent -->
      <div class="w-100" v-if="showChildren">
        <sidebar-tree :client-id="clientId" v-for="group in groupsForSidebar" :item="group" :key="`group_${group.id}`" resource="groups" />
		<li v-if="resource === 'clients' && groupsFilterActive" class="w-100">
			<div class="px-2 py-2 d-flex align-items-center font-size more-matches-text">
				<div class="mr-auto truncate" >
					<font-awesome-icon :icon="'search'" />
					{{ showingXOfYMatchesText }}
				</div>
			</div>
		</li>
		<li v-if="resource === 'clients' && groupsLength" class="w-100">
			<div class="px-2 py-2 d-flex align-items-center font-size client-text underlineHover">
				<div class="mr-auto truncate pointer" @click="redirectToGroupsPage" v-b-tooltip.hover :title="viewAllGroupsText">
					<font-awesome-icon icon="fa-solid fa-arrow-up-right-from-square" />
					View All Groups
				</div>
			</div>
		</li>
      </div>

      <!-- this second instance will be all devices within the group -->
      <div class="w-100" v-if="!root && showChildren">
        <sidebar-tree :client-id="clientId" v-for="device in devices" :item="device" :key="`device_${device.id}`" resource="devices" />
      </div>
    </div>
  </li>
</template>

<script>

export default {
	name: 'sidebar-tree',
	components: {
	},
	props: {
		root: {
			default: false,
			type: Boolean
		},
		item: {
			type: Object,
			required: true
		},
		resource: {
			required: true,
			type: String
		},
		clientId: {
			required: true,
			type: Number
		}
	},
	mounted () {
		if (this.resource === 'clients' && this.clientId === parseInt(this.$route.params.id)) {
			// if (this.$route.name.includes('unassigned')) {
			//   this.showChildren = true
			// } else {
			//   if (this.$route.params.groupId || this.$route.params.deviceId) {
			//     this.showChildren = true
			//   }
			// }
			this.showChildren = true;
		}
		else if (this.resource === 'groups' && this.item.client_id === parseInt(this.$route.params.id)) {
			if (this.item.id === parseInt(this.$route.params.groupId)) {
				this.showChildren = true;

				if (this.devices.length === 0 && this.groups.length === 0) {
					const request = {
						client: this.clientId,
						group: this.groupId
					};
					const action = 'sidebar/fetchGroup';

					this.$store.dispatch(action, request)
						.then(data => {
							// not handling data locally
						})
						.catch(err => {
							this.showErrorModal(err);
						})
						.finally(() => {
							// turn off loading
							this.loading = false;
						});
				}
			}
			else if (this.childGroupInPath) {
				this.showChildren = true;
			}
			else if (this.deviceInGroup) {
				this.showChildren = true;
			}
		}

		// TODO: This will be used to expand all in the future
		// if (this.clientId === parseInt(this.$route.params.id)) {
		//     this.showChildren = true
		// }
	},
	data () {
		return {
			showChildren: false,
			// determine whether or not to show spinner
			loading: false,
			unassigned: {
				id: null,
				name: 'Unassigned'
			},
			// whether or not we have determined if the resource has children
			childrenCalculated: false,
			groupNameFilter: '',
			searchGroupInput: ''
			// active: false
		};
	},
	computed: {
		groupsLength () {
			return this.groups.length;
		},

		groups () {
			if (this.root) {
				return this.$store.getters['sidebar/getGroups'].filter(element => {
					return element.parent_group_id === null && element.client_id === this.clientId;
				}).sort((a, b) => {
					if (a.name < b.name) {
						return -1;
					}

					if (a.name < b.name) {
						return 1;
					}

					return 0;
				});
			}

			if (this.resource === 'groups') {
				return this.$store.getters['sidebar/getGroups'].filter(element => {
					return element.parent_group_id === this.groupId;
				}).sort((a, b) => {
					if (a.name.toLowerCase() < b.name.toLowerCase()) {
						return -1;
					}

					if (a.name.toLowerCase() < b.name.toLowerCase()) {
						return 1;
					}

					return 0;
				});
			}

			return [];
		},

		filteredGroupsLength () {
			return this.filteredGroups.length;
		},

		filteredGroups () {
			if (this.root && this.groupsFilterActive) {
				const lowerCaseSearchTerm = this.groupNameFilter.toLowerCase();
				const filteredGroups = this.groups.filter(element =>
					element.name.toLowerCase().includes(lowerCaseSearchTerm)
				);
				return filteredGroups;
			}

			return this.groups;
		},

		routeGroupId () {
			return parseInt(this.$route.params.groupId);
		},

		groupsForSidebar () {
			if (this.root) {
				let groups;
				if (this.groupsFilterActive) {
					groups = this.filteredGroups.slice(0, this.filteredGroupsLimitForSidebar);
				}
				else {
					groups = this.filteredGroups.slice(0, this.defaultGroupsLimitForSidebar);
				}

				// if there is an active group and it is sliced out above
				if (this.clientId === parseInt(this.$route.params.id) && this.routeGroupId && !groups.find(group => group.id === this.routeGroupId)) {
					const activeGroup = this.groups.find(group => group.id === this.routeGroupId);

					if (activeGroup) {
						groups[Math.max(groups.length - 1, 0)] = activeGroup;

						groups.sort((a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1);
					}
				}

				return groups;
			}

			return this.filteredGroups;
		},

		groupsFilterActive () {
			return this.groupNameFilter.length >= this.minCharactersToSearch;
		},

		nShownGroups () {
			return this.groupsForSidebar.length;
		},

		devices () {
			if (this.resource === 'groups') {
				return this.$store.getters['sidebar/getDevices'].filter(element => {
					return element.group_id === this.groupId;
				}).sort((a, b) => {
					if (a.name.toLowerCase() < b.name.toLowerCase()) {
						return -1;
					}

					if (a.name.toLowerCase() < b.name.toLowerCase()) {
						return 1;
					}

					return 0;
				});
			}

			return [];
		},
		caretDisplay () {
			if (this.showChildren) {
				return 'caret-down';
			}

			return 'caret-right';
		},
		displayIcon () {
			if (this.resource === 'clients') {
				return 'building';
			}

			if (this.resource === 'groups') {
				return 'layer-group';
			}

			if (this.resource === 'unassigned') {
				return 'archive';
			}

			return 'desktop';
		},
		groupId () {
			if (this.resource === 'groups') {
				return this.item.id;
			}

			if (this.resource === 'devices') {
				return this.item.group_id;
			}

			return null;
		},
		routePath () {
			let params = {
				id: this.clientId
			};

			switch (this.resource) {
			case 'clients': {
				if (this.routePrefix === 'client') {
					return {
						name: this.routeName,
						params: params
					};
				}

				return {
					name: 'client',
					params: params
				};
			}
			case 'unassigned': {
				return `/clients/${this.clientId}/groups/unassigned`;
			}
			case 'groups': {
				params = { ...params, groupId: this.groupId };

				if (this.routePrefix === 'group' && this.routeSuffix !== 'unassigned') {
					return {
						name: this.$route.name,
						params: params
					};
				}

				return {
					name: 'group-devices',
					params: params
				};
			}
			default: {
				params = { ...params, groupId: this.groupId, deviceId: this.item.id };

				if (this.routePrefix === 'device' && this.routeSuffix === 'category') {
					let category = this.item.attribute_categories.find(element => element.id === this.$route.params.categoryId);
					if (category === undefined) {
						category = this.item.attribute_categories[0];
					}

					return {
						name: this.$route.name,
						params: {
							...params,
							categoryId: category.id
						}
					};
				}

				if (this.routePrefix === 'device') {
					return {
						name: this.$route.name,
						params: params
					};
				}
				const returnObject = {
					name: 'device-category',
					params: {
						...params
					}
				};

				if (this.item.attribute_categories.count) {
					returnObject.params.categoryId = this.item.attribute_categories[0].id;
				}

				return returnObject;
			}
			}
		},
		active () {
			// if not the correct client, it cannot be active
			if (this.clientId !== parseInt(this.$route.params.id)) {
				return false;
			}

			// if the resource type is client
			if (this.resource === 'clients') {
				if (this.groupId) {
					return false;
				}

				if (this.$route.name.includes('unassigned')) {
					return false;
				}

				return this.$route.name.includes('client');
			}

			// if the resource type is group
			if (this.resource === 'groups') {
				if (this.$route.name === 'group-devices') {
					return this.item.id === parseInt(this.$route.params.groupId);
				}

				if (this.$route.name.includes('device')) {
					return false;
				}

				return this.item.id === parseInt(this.$route.params.groupId);
			}

			// if the resource type is unassigned
			if (this.$route.name.includes('unassigned') && this.resource === 'unassigned') {
				return true;
			}

			// if the resource type is device
			if (this.resource === 'devices') {
				if (this.$route.name.includes('group')) {
					return false;
				}

				if (this.$route.name.includes('device')) {
					if (this.item.child_ids && this.item.child_ids.length > 0 && this.item.child_ids.includes(parseInt(this.$route.params.deviceId))) {
						return true;
					}

					return this.item.id === parseInt(this.$route.params.deviceId);
				}

				return false;
			}

			return false;
		},
		hasChildren () {
			if (this.root) {
				return true;
			}

			if (this.resource === 'unassigned' || this.resource === 'devices') {
				return false;
			}

			if (!this.childrenCalculated) {
				return true;
			}

			return this.groups.length > 0 || this.devices.length > 0;
		},
		clientColor () {
			return this.resource === 'clients';
		},
		groupColor () {
			return this.resource === 'unassigned' || this.resource === 'groups';
		},
		deviceColor () {
			return this.resource === 'devices';
		},
		rootClass () {
			return {
				active: this.active,
				'client-text': this.clientColor,
				'group-text': this.groupColor,
				'device-text': this.deviceColor
			};
		},
		routeName () {
			return this.$route.name;
		},
		routePrefix () {
			return this.routeName.split('-')[0];
		},
		routeSuffix () {
			return this.routeName.split('-')[1];
		},
		/**
     * Determine if the selected group is a child of 'this' group
     *
     * Since we only have 1 group id in the uri, this is used to determine
     * whether or not a group should be expanded, based on a child group being
     * active
     *
     * Active group is the group that is selected
     *
     * @returns {boolean}
     */
		childGroupInPath () {
			if (!this.hasChildren) {
				return false;
			}

			return this.getNestedGroups().includes(parseInt(this.$route.params.groupId));
		},
		/**
     * Determine if the selected device is in 'this' group
     *
     * @return {boolean}
     */
		deviceInGroup () {
			if (this.$route.params.deviceId) {
				const device = this.$store.getters['sidebar/getDevices'].find(element => {
					return element.id === parseInt(this.$route.params.deviceId);
				});

				return device && device.group_id === this.item.id;
			}

			return false;
		},
		/**
     * get if we should allow sidebar navigation
     *
     * @return {boolean}
     */
		allowNavigation () {
			return this.$store.getters['sidebar/getAllowNavigation'];
		},
		defaultGroupsLimitForSidebar () {
			return this.$store.getters['sidebar/getDefaultGroupsLimitForSidebar'];
		},
		filteredGroupsLimitForSidebar () {
			return this.$store.getters['sidebar/getFilteredGroupsLimitForSidebar'];
		},
		minCharactersToSearch () {
			return this.$store.getters['sidebar/getMinCharactersToSearch'];
		},
		showingXOfYMatchesText () {
			return `Showing ${this.nShownGroups} of ${this.filteredGroupsLength} matches`;
		},

		viewAllGroupsText () {
			return `View ${this.groupsLength} parent group(s) + nested group(s)`;
		}
	},
	watch: {
		$route (from, to) {
			if (this.resource === 'clients' && this.clientId === parseInt(this.$route.params.id)) {
				if (this.$route.name.includes('unassigned')) {
					this.showChildren = true;
				}
				else {
					if (this.$route.params.groupId || this.$route.params.deviceId) {
						this.showChildren = true;
					}
				}
			}
			else if (this.resource === 'groups' && this.item.client_id === parseInt(this.$route.params.id)) {
				if (this.childGroupInPath) {
					this.showChildren = true;
				}
				else if (this.deviceInGroup) {
					this.showChildren = true;
				}
			}

			// TODO: This will be used to expand all in the future
			// if (from.params.id !== to.params.id) {
			//   this.showChildren = this.clientId === parseInt(this.$route.params.id)
			// }
		}
	},
	methods: {
		/**
     * Show children of 'this' resource
     *
     * @return {void}
     */
		toggleChildren () {
			// mark that we have tried to get/display children
			this.childrenCalculated = true;

			// if we are going from a hidden state to visible state
			if (!this.showChildren === true) {
				// turn on loading
				this.loading = true;
				// used to determine if we need to fetch any data
				let apiCall = false;
				// start the request object, we will always need a client
				const request = { client: this.clientId };
				// initialize the action string
				let action = '';

				// if the resource we are looking at is a client
				if (this.resource === 'clients') {
					// if there are no known groups for the client
					if (this.groups.length === 0) {
						// we need to make an api call
						apiCall = true;
						// the action will be fetching client data
						action = 'sidebar/fetchClient';
					}
				}
				else if (this.resource === 'groups') {
					// if the resource we are looking at is a group
					// if there are no known devices and child groups
					if (this.devices.length === 0 && this.groups.length === 0) {
						// we need to make an api call
						apiCall = true;
						// add the group to the request object
						request.group = this.groupId;
						// the action will be fetching group data
						action = 'sidebar/fetchGroup';
					}
				}

				// if we have determined that we need to retrieve data from the core
				if (apiCall) {
					// dispatch selected action with request object
					this.$store.dispatch(action, request)
						.then(data => {
							// not handling data locally
						})
						.catch(err => {
							this.showErrorModal(err);
						})
						.finally(() => {
							// turn off loading
							this.loading = false;
						});
				}
				else {
					// we already have data that we need
					// turn off loading
					this.loading = false;
				}
			}

			// complete the toggle
			this.showChildren = !this.showChildren;
		},
		/**
     * Navigate to 'this' resource
     *
     * @return {void}
     */
		goToResource () {
			if (this.allowNavigation) {
				// if resource is not expanded, expand it
				if (this.showChildren === false) {
					this.toggleChildren();
				}

				// navigate to the 'this' resource
				this.$router.push(this.routePath);
			}
		},
		/**
     * Get all groups within a group based on id
     *
     * @param id
     * @returns {[]}
     */
		getNestedGroups (id = null) {
			if (id === null) {
				id = this.item.id;
			}

			let idArray = [];

			this.$store.getters['sidebar/getGroups'].filter(element => {
				return element.parent_group_id === id;
			}).forEach(element => {
				idArray.push(element.id);
				idArray = idArray.concat(this.getNestedGroups(element.id));
			});

			return idArray;
		},

		searchBoxUpdated () {
			if (this.searchGroupInput === '') {
				this.groupNameFilter = '';
			}
		},

		updateSearchTerm () {
			this.groupNameFilter = this.searchGroupInput;
		},

		redirectToGroupsPage () {
			if (this.allowNavigation) {
				this.$router.push(`/clients/${this.clientId}/groups`);
			}
		}
	}
};
</script>

<style scoped>
  .font-size {
    font-size: 13px;
  }

  .active {
    background-color: rgba(128, 128, 128, 0.25)
  }

  .truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .pointer {
    cursor: pointer;
  }

  .client-text {
    color: #001786;
  }

  .group-text {
    color: #0082c6;
  }

  .device-text {
    color: #231f20;
  }

  .child-indent {
    padding-left: 0.75rem;
  }

  .underlineHover:hover {
	text-decoration: underline;
  }

  .more-matches-text {
	color: #777781;
  }
</style>
